import React, { useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calculator } from "lucide-react";

const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const CALCS = [
  { type: "premium_convert", titleKey: "calc_premium_convert", fields: [{ k: "annual", labelKey: "fld_annual" }] },
  { type: "deductible_example", titleKey: "calc_deductible_example", fields: [{ k: "loss", labelKey: "fld_loss" }, { k: "deductible", labelKey: "fld_deductible" }] },
  { type: "coinsurance", titleKey: "calc_coinsurance", fields: [{ k: "bill", labelKey: "fld_bill" }, { k: "deductible", labelKey: "fld_deductible" }, { k: "coinsurance", labelKey: "fld_coinsurance" }] },
  { type: "premium_increase", titleKey: "calc_premium_increase", fields: [{ k: "old", labelKey: "fld_old" }, { k: "new", labelKey: "fld_new" }] },
  { type: "life_need", titleKey: "calc_life_need", fields: [{ k: "income", labelKey: "fld_income" }, { k: "years", labelKey: "fld_years" }, { k: "debts", labelKey: "fld_debts" }] },
];

export default function Calculators() {
  const { t } = useI18n();
  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Calculator className="h-7 w-7 text-brandblue" /> {t("calculators")}</h1>
      <p className="mt-1 text-slate-500">{t("calculators_sub")}</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {CALCS.map((c) => <CalcCard key={c.type} calc={c} t={t} />)}
      </div>
    </AppLayout>
  );
}

function CalcCard({ calc, t }) {
  const [vals, setVals] = useState({});
  const [result, setResult] = useState(null);

  const run = async () => {
    const values = {};
    calc.fields.forEach((f) => { if (vals[f.k] !== undefined && vals[f.k] !== "") values[f.k] = parseFloat(vals[f.k]); });
    try { const r = await api.post("/calculators", { type: calc.type, values }); setResult(r.data); }
    catch { setResult({ error: t("calc_fill") }); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-100 card-shadow p-6">
      <h3 className="font-semibold text-navy">{t(calc.titleKey)}</h3>
      <div className="mt-3 grid grid-cols-2 gap-3">
        {calc.fields.map((f) => (
          <div key={f.k}><Label className="text-xs">{t(f.labelKey)}</Label>
            <Input data-testid={`calc-${calc.type}-${f.k}`} type="number" className="mt-1 rounded-xl" value={vals[f.k] || ""} onChange={(e) => setVals({ ...vals, [f.k]: e.target.value })} /></div>
        ))}
      </div>
      <Button data-testid={`calc-run-${calc.type}`} onClick={run} className="mt-3 rounded-full brand-gradient text-white">{t("calculate")}</Button>
      {result && !result.error && (
        <div className="mt-4 rounded-xl bg-blue-50/60 p-4 text-sm space-y-1">
          {Object.entries(result).map(([k, v]) => (
            <div key={k} className="flex justify-between"><span className="text-slate-500 capitalize">{k.replace(/_/g, " ")}</span>
              <span className="font-semibold text-navy">{typeof v === "number" ? (k.includes("percent") ? `${v}%` : money(v)) : String(v)}</span></div>
          ))}
        </div>
      )}
      {result?.error && <p className="mt-3 text-sm text-red-500">{result.error}</p>}
    </div>
  );
}
